import { useState } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ShieldCheck,
  CreditCard,
  Upload,
  Camera,
  Globe,
  Users,
} from "lucide-react";
import { useQuery, useMutation } from "react-query";
import {
  getBorrowerById,
  updateMyBorrower,
  updateMyBorrowerInfo,
} from "../../sdk/borrower/borrower";
import { useError } from "../../context/ErrorProvider";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "../../shared/Loading";
import { InputField } from "../../shared/NewInput";
import { uploadIdentification } from "../../sdk/kyc/kyc";
import { uploadImage } from "../../sdk/upload/upload";

const EditBorrower = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useError();
  const [formData, setFormData] = useState({
    firstname: "",
    middlename: "",
    lastname: "",
    identification: "",
    identification_type: "",
    country_of_residence: "",
    dob: "",
    citizenship: "",
    gender: "",
    postal_address: "",
    altPhoneNumber: "",
    email: "",
    dependants: "",
    mobileno: "",
    username: "",
    id_front_image: "",
    selfie_image: "",
  });

  const [isUploadingId, setIsUploadingId] = useState(false);
  const [isUploadingSelfie, setIsUploadingSelfie] = useState(false);

  useQuery({
    queryKey: ["get borrower", id],
    queryFn: async () => {
      const response = await getBorrowerById(id);
      return response.data.data;
    },
    onSuccess: (data) => {
      setFormData({
        firstname: data?.firstname ?? "",
        middlename: data?.middlename ?? "",
        lastname: data?.lastname ?? "",
        dob: data?.dob ?? "",
        identification: data?.identification ?? "",
        identification_type: data?.identification_type ?? "",
        mobileno: data?.mobileno ?? "",
        email: data?.email ?? "",
        username: data?.username ?? "",
        dependants: data?.dependants ?? "",
        postal_address: data?.postal_address ?? "",
        citizenship: data?.citizenship ?? "",
        gender: data?.gender ?? "",
        altPhoneNumber: data?.altPhoneNumber ?? "",
        country_of_residence: data?.country_of_residence ?? "",
        id_front_image: data?.id_front_image ?? "",
        selfie_image: data?.selfie_image ?? "",
      });
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { mutate: updateBorrower, isLoading } = useMutation({
    mutationKey: ["update borrower"],
    mutationFn: () =>
      updateMyBorrowerInfo(
        id,
        formData?.firstname,
        formData?.middlename,
        formData?.lastname,
        formData?.identification,
        formData?.identification_type,
        formData?.country_of_residence,
        formData?.dob,
        formData?.citizenship,
        formData?.gender,
        formData?.postal_address,
        formData?.altPhoneNumber,
        formData?.email,
        formData?.dependants,
        formData?.mobileno,
        formData?.username,
        formData?.id_front_image,
        formData?.selfie_image,
      ),
    onSuccess: () => {
      navigate(`/dashboard/borrowers/${id}`, { replace: true });
    },
    onError: (error) => {
      showError(error?.response?.data?.message || error.message);
    },
  });

  const handleUpdateBorrower = async (e) => {
    e.preventDefault();
    await updateBorrower();
  };

  function formatDate(dateStr, defaultValue = "") {
    if (typeof dateStr !== "string") return defaultValue;

    const parts = dateStr.split(".");
    if (parts.length !== 3) return defaultValue;

    const [day, month, year] = parts;
    const isValid =
      /^\d{2}$/.test(day) &&
      /^\d{2}$/.test(month) &&
      /^\d{4}$/.test(year) &&
      Number(day) >= 1 &&
      Number(day) <= 31 &&
      Number(month) >= 1 &&
      Number(month) <= 12;

    if (!isValid) return defaultValue;

    return `${year}-${month}-${day}`;
  }

  const handleUploadIdentification = async (file) => {
    try {
      const identificationInfo = await uploadIdentification(file);
      let splitName = identificationInfo?.fullNames?.trim()?.split(" ");
      setFormData({
        firstname: splitName[0] || "",
        middlename: splitName[1] || "",
        lastname: splitName[2] || "",
        identification: identificationInfo?.idNumber || "",
        dob: formatDate(identificationInfo?.dateOfBirth, "2000-01-01") || "",
        gender: identificationInfo?.sex || "",
      });
    } catch (error) {
      showError(error?.response?.data?.message || error.message);
    }
  };

  return (
    <div className="min-h-screen font-sans text-gray-900">
      <p className="my-2 text-[#000000] text-[20px] font-medium">
        Edit Borrower Information
      </p>
      <div className="bg-white">
        <div className="overflow-hidden">
          <form
            onSubmit={handleUpdateBorrower}
            className="divide-y divide-gray-100"
          >
            {/* Section: ID & Personal Details */}
            <div className="p-3">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <User className="text-blue-600" size={24} />
                </div>
                <h2 className="text-xl font-bold">Identity Details</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <InputField
                  label="Username"
                  name="username"
                  value={formData.username}
                  icon={User}
                  onChange={handleChange}
                />
                <InputField
                  label="First Name"
                  name="firstname"
                  value={formData.firstname}
                  icon={User}
                  onChange={handleChange}
                />
                <InputField
                  label="Middle Name"
                  name="middlename"
                  value={formData.middlename}
                  icon={User}
                  onChange={handleChange}
                />
                <InputField
                  label="Last Name"
                  name="lastname"
                  value={formData.lastname}
                  icon={User}
                  onChange={handleChange}
                />
                <div className="flex flex-col gap-2 w-full">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    ID Type
                  </label>
                  <div className="relative flex items-center">
                    <div className="absolute left-3 text-gray-400 pointer-events-none">
                      <ShieldCheck size={18} />
                    </div>
                    <select
                      name="identification_type"
                      value={formData.identification_type}
                      onChange={handleChange}
                      className="w-full h-[50px] pl-10 pr-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all appearance-none cursor-pointer"
                    >
                      <option value="" disabled>
                        Select ID Type
                      </option>
                      <option value="National ID">National ID</option>
                      <option value="Passport">Passport</option>
                    </select>
                  </div>
                </div>
                <InputField
                  label="ID Number"
                  name="identification"
                  value={formData.identification}
                  icon={CreditCard}
                  onChange={handleChange}
                />
                <InputField
                  label="Date of Birth"
                  name="dob"
                  type="date"
                  value={formData.dob}
                  icon={Calendar}
                  onChange={handleChange}
                />
                <div className="flex flex-col gap-2 w-full">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Gender
                  </label>
                  <div className="relative flex items-center">
                    <div className="absolute left-3 text-gray-400 pointer-events-none">
                      <Users size={18} />
                    </div>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      className="w-full h-[50px] pl-10 pr-4 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 font-medium focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all appearance-none cursor-pointer"
                    >
                      <option value="" disabled>
                        Select Gender
                      </option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                </div>
                <InputField
                  label="Citizenship"
                  name="citizenship"
                  value={formData.citizenship}
                  icon={Globe}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Section: Contacts & Residency */}
            <div className="p-3 bg-gray-50/50">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <MapPin className="text-blue-600" size={24} />
                </div>
                <h2 className="text-xl font-bold">Contacts & Residency</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <InputField
                  label="Phone Number"
                  name="mobileno"
                  value={formData.mobileno}
                  icon={Phone}
                  onChange={handleChange}
                />
                <InputField
                  label="Alternate Phone Number"
                  name="altPhoneNumber"
                  value={formData.altPhoneNumber}
                  icon={Phone}
                  onChange={handleChange}
                />
                <InputField
                  label="Email Address"
                  name="email"
                  value={formData.email}
                  icon={Mail}
                  onChange={handleChange}
                />
                <InputField
                  label="Country"
                  name="country_of_residence"
                  value={formData.country_of_residence}
                  icon={MapPin}
                  onChange={handleChange}
                />
                <InputField
                  label="Postal Address"
                  name="postal_address"
                  value={formData.postal_address}
                  icon={MapPin}
                  onChange={handleChange}
                />
                <InputField
                  label="Dependants"
                  name="dependants"
                  value={formData.dependants}
                  icon={User}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Section: KYC Document Uploads */}
            <div className="p-3">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <Camera className="text-blue-600" size={24} />
                </div>
                <h2 className="text-xl font-bold">
                  Biometrics & Document Verification
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* 1. NATIONAL ID CARD UPLOAD */}
                <div className="group relative">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">
                    National ID (Front View)
                  </p>

                  {formData?.id_front_image ? (
                    /* STATE A: IMAGE IS PRESENT (Show Preview + Replace Option) */
                    <div className="relative border border-slate-100 rounded-2xl p-2 bg-slate-50/50 overflow-hidden h-64 flex flex-col items-center justify-center shadow-sm">
                      <img
                        src={formData.id_front_image}
                        className="w-full h-full object-contain rounded-xl transition-transform duration-300 group-hover:scale-[1.01]"
                        alt="ID Front Preview"
                      />

                      {/* Hover / Uploading Mask Overlay Grid Box */}
                      <div
                        className={`absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] flex flex-col items-center justify-center transition-all duration-200 ${
                          isUploadingId
                            ? "opacity-100 cursor-wait"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                      >
                        {isUploadingId ? (
                          <div className="flex flex-col items-center gap-2 text-white">
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-[11px] font-bold uppercase tracking-wider">
                              Uploading Asset...
                            </span>
                          </div>
                        ) : (
                          <label className="bg-white text-slate-800 px-4 py-2.5 rounded-xl flex items-center gap-2 font-bold text-xs shadow-xl cursor-pointer hover:bg-slate-50 active:scale-95 transition-all">
                            <Upload size={14} strokeWidth={2.5} />
                            Replace Front Image
                            <input
                              type="file"
                              accept="image/*"
                              disabled={isUploadingId}
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setIsUploadingId(true);
                                  try {
                                    await handleUploadIdentification(file);
                                    const response = await uploadImage(file);
                                    setFormData((prev) => ({
                                      ...prev,
                                      id_front_image: response?.url,
                                    }));
                                  } catch (err) {
                                    console.error("Upload failed", err);
                                  } finally {
                                    setIsUploadingId(false);
                                  }
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* STATE B: NO IMAGE PRESENT (Show Clean Empty State Dropzone to Add) */
                    <label
                      className={`relative border-2 border-dashed border-slate-200 rounded-2xl p-6 bg-white transition-all duration-200 h-64 flex flex-col items-center justify-center text-center group ${
                        isUploadingId
                          ? "cursor-wait opacity-80"
                          : "cursor-pointer hover:bg-slate-50/50 hover:border-blue-500"
                      }`}
                    >
                      {isUploadingId ? (
                        <div className="flex flex-col items-center gap-2 text-slate-500">
                          <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                          <h4 className="text-xs font-bold tracking-tight mt-2 text-slate-700">
                            Uploading to Server
                          </h4>
                          <p className="text-[10px] font-medium text-slate-400">
                            Securing remote asset link strings...
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100 mb-3 shadow-inner group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-100 transition-all">
                            <Upload size={20} strokeWidth={2} />
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-xs font-bold text-slate-700 tracking-tight">
                              Upload Document Asset
                            </h4>
                            <p className="text-[10px] font-medium text-slate-400 max-w-[180px] leading-normal">
                              Drag and drop or click to browse JPG, PNG file
                              formats up to 5MB.
                            </p>
                          </div>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={isUploadingId}
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setIsUploadingId(true);
                            try {
                              await handleUploadIdentification(file);
                              const response = await uploadImage(file);
                              setFormData((prev) => ({
                                ...prev,
                                id_front_image: response?.url,
                              }));
                            } catch (err) {
                              console.error("Upload failed", err);
                            } finally {
                              setIsUploadingId(false);
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* 2. LIVE SELFIE / PORTRAIT UPLOAD */}
                <div className="group relative">
                  <p className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">
                    Live Selfie / Portrait
                  </p>

                  {formData?.selfie_image ? (
                    /* STATE A: IMAGE IS PRESENT (Show Preview + Replace Option) */
                    <div className="relative border border-slate-100 rounded-2xl p-2 bg-slate-50/50 overflow-hidden h-64 flex flex-col items-center justify-center shadow-sm">
                      <img
                        src={formData.selfie_image}
                        className="w-full h-full object-contain rounded-xl transition-transform duration-300 group-hover:scale-[1.01]"
                        alt="Selfie Portrait Preview"
                      />

                      {/* Hover / Uploading Mask Overlay Grid Box */}
                      <div
                        className={`absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] flex flex-col items-center justify-center transition-all duration-200 ${
                          isUploadingSelfie
                            ? "opacity-100 cursor-wait"
                            : "opacity-0 group-hover:opacity-100"
                        }`}
                      >
                        {isUploadingSelfie ? (
                          <div className="flex flex-col items-center gap-2 text-white">
                            <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            <span className="text-[11px] font-bold uppercase tracking-wider">
                              Uploading Asset...
                            </span>
                          </div>
                        ) : (
                          <label className="bg-white text-slate-800 px-4 py-2.5 rounded-xl flex items-center gap-2 font-bold text-xs shadow-xl cursor-pointer hover:bg-slate-50 active:scale-95 transition-all">
                            <Upload size={14} strokeWidth={2.5} />
                            Upload New Photo
                            <input
                              type="file"
                              accept="image/*"
                              disabled={isUploadingSelfie}
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setIsUploadingSelfie(true);
                                  try {
                                    const response = await uploadImage(file);
                                    setFormData((prev) => ({
                                      ...prev,
                                      selfie_image: response?.url,
                                    }));
                                  } catch (err) {
                                    console.error("Upload failed", err);
                                  } finally {
                                    setIsUploadingSelfie(false);
                                  }
                                }
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* STATE B: NO IMAGE PRESENT (Show Clean Empty State Dropzone to Add) */
                    <label
                      className={`relative border-2 border-dashed border-slate-200 rounded-2xl p-6 bg-white transition-all duration-200 h-64 flex flex-col items-center justify-center text-center group ${
                        isUploadingSelfie
                          ? "cursor-wait opacity-80"
                          : "cursor-pointer hover:bg-slate-50/50 hover:border-blue-500"
                      }`}
                    >
                      {isUploadingSelfie ? (
                        <div className="flex flex-col items-center gap-2 text-slate-500">
                          <div className="w-8 h-8 border-2 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
                          <h4 className="text-xs font-bold tracking-tight mt-2 text-slate-700">
                            Uploading to Server
                          </h4>
                          <p className="text-[10px] font-medium text-slate-400">
                            Securing remote asset link strings...
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="w-12 h-12 bg-slate-50 text-slate-400 rounded-xl flex items-center justify-center border border-slate-100 mb-3 shadow-inner group-hover:bg-blue-50 group-hover:text-blue-600 group-hover:border-blue-100 transition-all">
                            <Upload size={20} strokeWidth={2} />
                          </div>
                          <div className="space-y-1">
                            <h4 className="text-xs font-bold text-slate-700 tracking-tight">
                              Upload Portrait Verification
                            </h4>
                            <p className="text-[10px] font-medium text-slate-400 max-w-[180px] leading-normal">
                              Drag and drop or click to browse JPG, PNG file
                              formats up to 5MB.
                            </p>
                          </div>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        disabled={isUploadingSelfie}
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setIsUploadingSelfie(true);
                            try {
                              const response = await uploadImage(file);
                              setFormData((prev) => ({
                                ...prev,
                                selfie_image: response?.url,
                              }));
                            } catch (err) {
                              console.error("Upload failed", err);
                            } finally {
                              setIsUploadingSelfie(false);
                            }
                          }
                        }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-3 bg-white border-t border-gray-100 flex justify-end gap-4">
              <button
                type="button"
                className="px-8 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold hover:bg-gray-50 transition-all"
              >
                Discard Changes
              </button>
              <button
                type="submit"
                onClick={handleUpdateBorrower}
                disabled={isLoading}
                className="px-10 py-3 flex items-center justify-center gap-2 rounded-xl bg-black text-white font-bold hover:bg-gray-800 shadow-lg shadow-black/20 transform active:scale-95 transition-all"
              >
                {isLoading && <Loading />}
                Save Profile Data
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditBorrower;
